import { FC, Fragment } from 'react'
import { SectionArticlesTitle } from './styles'
import { RenewalData } from 'src/dtos/renewalData'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { IoMdCheckmark } from 'react-icons/io'
import AreasOfPractice from 'src/components/application/mainPagesComponents/Firm/AreasOfPractice'
import { TbFileCheck } from 'react-icons/tb'
import { FirmDetailsContactData } from 'src/dtos/portal'

interface FirmSectionProps {
  renewalData?: RenewalData;
  isComplete: boolean;
  primaryContactData : FirmDetailsContactData | undefined;
}

const RenewalFirmSection: FC<FirmSectionProps> = ({ renewalData, isComplete, primaryContactData }) => {

  const firmSection = renewalData?.firmSection
  const transactions = firmSection?.securitiesLawInfo?.transactions

  const hasRealEstate = firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
  const hasSecuritiesLaw = firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt' || a.area === 'Securities - Other Types Of Securities Practice')

  const areasOfPractice = renewalData?.firmSection?.practiceAreas

  return (
    <section>
      <header>
        <h4>Firm</h4>
        {isComplete ? (
          <h6><IoMdCheckmark /> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>FIRM BASICS</SectionArticlesTitle>
      <article>
        <strong>Firm Name</strong>
        <span>{firmSection?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Type of Practice</strong>
        <span>{firmSection?.practiceType ?? 'N/A'}</span>
      </article>
      {firmSection?.establishmentDate != null ?
      <article>
        <strong>Firm Established</strong>
        <span>{firmSection?.establishmentDate}</span>
      </article> : <div></div>
      }
      <article>
        <strong>Contact Information</strong>
        <span>{primaryContactData?.firstName + ' ' + primaryContactData?.lastName ?? 'N/A'}</span>
        <span>{primaryContactData?.emailAddress ?? 'N/A'}</span>
        <span>{firmSection?.contactInfo?.phoneNumber ?? 'N/A'}</span>
        <span>{firmSection?.contactInfo?.website ?? ''}</span>
        <span>{firmSection?.contactInfo?.faxNumber ?? ''}</span>
      </article>
      <article>
        <strong>Principal Mailing Address</strong>
        <span>{firmSection?.mailAddress?.formattedAddress ?? 'N/A'}</span>
        <span>{firmSection?.mailAddress?.suiteNumber ?? ''}</span>
      </article>
      {firmSection?.isBillingEqualToMail === false ? (
        <article>
          <strong>Billing Address</strong>
          <span>{firmSection?.billingAddress?.formattedAddress}</span>
          <span>{firmSection?.billingAddress?.suiteNumber}</span>
        </article>
      ) : null}
      {!!firmSection?.isSecondaryContactAdded === true ? (
        <article>
          <strong>Secondary Contact</strong>
          <span>{[firmSection?.secondaryContact?.firstName, firmSection?.secondaryContact?.lastName].join(' ') ?? 'N/A'}</span>
          <span>{firmSection?.secondaryContact?.emailAddress ?? ''}</span>
          <span>{firmSection?.secondaryContact?.phoneNumber ?? ''}</span>
          {firmSection?.secondaryContact?.isFirmAdministrator === true ? (
            <span><IoIosCheckmarkCircle /> Firm Administrator</span>
          ) : null}
        </article>
      ) : null}
      <article>
        <strong>Number of Offices</strong>
        <span>{firmSection?.numberOfOffices ?? 'N/A'}</span>
      </article>
      <SectionArticlesTitle>AREAS OF PRACTICE</SectionArticlesTitle>
      <article>
        {areasOfPractice && areasOfPractice.length > 0 ? (
          <AreasOfPractice practiceAreas={areasOfPractice} />
        ) : <span>N/A</span>}
      </article>
      {!!hasRealEstate ? (
        <>
          <SectionArticlesTitle>REAL ESTATE</SectionArticlesTitle>
          <article>
            <strong>Is any firm member a licensed escrow officer for a title company?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberWithEscrowLicense)}</span>
          </article>
          <article>
            <strong>Does Applicant or any firm member have any agreements with title companies?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies)}</span>
            {firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies === true ? firmSection?.realEstateInfo?.agreementWithTitleCompaniesFile?.map(file =>
              <span key={file?.fileName}>{file?.fileName ?? 'N/A'}</span>
            ) : null}
          </article>
          <article>
            <strong>Is any firm member a fee attorney for a title company?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberFeeAttorney)}</span>
          </article>
          <article>
            <strong>Is any firm member an outside closing attorney, approved attorney or P-22 attorney?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberOutsideClosingAttorney)}</span>
          </article>
          <article>
            <strong>How many non-attorney licensed escrow officers are currently employed by your firm?</strong>
            <span>{firmSection?.realEstateInfo?.nonAttorneysWithEscrowLicenseQuantity ?? 'N/A'}</span>
          </article>
        </>
      ) : null}
      {!!hasSecuritiesLaw ? (
        <>
          <SectionArticlesTitle>SECURITIES LAW</SectionArticlesTitle>
          {firmSection?.securitiesLawInfo?.hasFiles === true ? (
            <>
              <article>
                <strong>
                  Uploaded files of securities transactions
                </strong>
                {firmSection?.securitiesLawInfo?.securitiesLawFiles?.map(file => (
                  <span key={file.fileName}>{file.fileName}</span>
                ))}
              </article>
            </>
          ) : (
            <>
              {transactions && transactions.length > 0 ? transactions?.map((transaction, index) =>
                <Fragment key={index}>
                  <article><span><h6>Transaction {index + 1}</h6></span></article>
                  <article>
                    <strong>
                      Date
                    </strong>
                    <span>{transaction?.date ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Client name
                    </strong>
                    <span>{transaction?.clientName ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Size of offering
                    </strong>
                    <span>{transaction?.offeringSize ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Role in transaction
                    </strong>
                    <span>{transaction?.transactionRole ?? 'N/A'}</span>
                  </article>
                </Fragment>
              ) : null}
            </>
          )}
        </>
      ) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{firmSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!firmSection?.review?.files && firmSection?.review?.files.length > 0 ? 
          firmSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default RenewalFirmSection